import React, { useState, useEffect } from "react";
import {
  Stack,
  Typography,
  TextField,
  IconButton,
  Button,
  Alert,
  Link,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

const EmailSettings = ({ userData, setSnackbar }) => {
  const [emailNotifications, setEmailNotifications] = useState([""]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (userData?.leadEmails && Array.isArray(userData.leadEmails)) {
      setEmailNotifications(
        userData.leadEmails.length > 0 ? userData.leadEmails : [""]
      );
    }
  }, [userData]);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailAdd = () => {
    if (emailNotifications.length < 5)
      setEmailNotifications([...emailNotifications, ""]);
  };

  const handleEmailChange = (index, value) => {
    const newEmails = [...emailNotifications];
    newEmails[index] = value;
    setEmailNotifications(newEmails);
  };

  const handleEmailRemove = (index) => {
    if (emailNotifications.length > 1) {
      setEmailNotifications(emailNotifications.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validEmails = emailNotifications.filter((email) => email.trim());

    if (validEmails.length === 0) {
      setSnackbar({
        open: true,
        message: "Please add at least one email address",
        severity: "warning",
      });
      return;
    }

    if (validEmails.some((email) => !isValidEmail(email))) {
      setSnackbar({
        open: true,
        message: "Please enter valid email addresses",
        severity: "error",
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_ENDPOINT}/user/assign-leads-emails`, {
        method: "POST",
        ...getHeader(),
        body: JSON.stringify({ emails: validEmails }),
      });

      if (!response.ok) throw new Error();
      setSnackbar({
        open: true,
        message: "Email addresses updated successfully!",
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to update email addresses.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <Typography variant="h6" fontWeight="bold">
          Email Notifications
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage email addresses for lead notifications
        </Typography>

        {emailNotifications.map((email, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="center">
            <TextField
              fullWidth
              size="small"
              type="email"
              placeholder="Enter email address"
              value={email}
              onChange={(e) => handleEmailChange(index, e.target.value)}
              error={Boolean(email.trim() && !isValidEmail(email))}
              helperText={
                email.trim() && !isValidEmail(email)
                  ? "Invalid email format"
                  : ""
              }
            />
            {emailNotifications.length > 1 && (
              <IconButton
                color="error"
                onClick={() => handleEmailRemove(index)}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ))}

        {emailNotifications.length < 5 ? (
          <Button
            onClick={handleEmailAdd}
            startIcon={<AddIcon />}
            size="small"
            sx={{ alignSelf: "flex-start" }}
          >
            Add Email
          </Button>
        ) : (
          <Alert severity="info">
            Maximum 5 emails.{" "}
            <Link href="/home/help-feedback">Contact Admin</Link>
          </Alert>
        )}

        <Stack
          direction="row"
          sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}
        >
          <LoadingButton
            type="submit"
            variant="contained"
            startIcon={<CheckIcon />}
            loading={submitting}
          >
            Save Changes
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
};

export default EmailSettings;
